import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Divider, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../auth';
import { PageHeader } from '../components/page-elements';

const CreateCustomers = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    customerCode: '',
    customerAddress1: '',
    customerAddress2: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    customerLogo: '', // Will hold the base64 string or URL of the uploaded image.
    enableGroundInventory: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle text field changes.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle logo file upload.
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          customerLogo: reader.result, // Save the base64 string.
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit handler to POST the new customer.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(formData),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.errors?.[0]?.msg || 'Failed to create customer');
      }
      setSuccess('Customer created successfully!');
      // Optionally reset the form fields.
      setFormData({
        customerName: '',
        customerCode: '',
        customerAddress1: '',
        customerAddress2: '',
        customerCity: '',
        customerState: '',
        customerZip: '',
        customerLogo: '',
        enableGroundInventory: false,
      });
      setLogoFile(null);
      if (data?.customer?._id) {
        navigate(`/customers/customer/${data.customer._id}`);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <Box p={2}>
      <PageHeader
        title="Create Customer"
        subtitle="Create customer profiles for external accounts or internal operations."
      />
      {error && <Typography color="error">{error}</Typography>}
      {success && <Typography color="primary">{success}</Typography>}
      <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Company Name"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Customer Code"
                  name="customerCode"
                  value={formData.customerCode}
                  onChange={handleChange}
                  fullWidth
                  helperText="Optional. Use for external railcar extract matching."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Company Address 1"
                  name="customerAddress1"
                  value={formData.customerAddress1}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Company Address 2"
                  name="customerAddress2"
                  value={formData.customerAddress2}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Company City"
                  name="customerCity"
                  value={formData.customerCity}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Company State"
                  name="customerState"
                  value={formData.customerState}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Company ZIP"
                  name="customerZip"
                  value={formData.customerZip}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(formData.enableGroundInventory)}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, enableGroundInventory: Boolean(e.target.checked) }))
                      }
                    />
                  )}
                  label="Enable Ground Inventory Conversion and Allocation"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Button variant="outlined" component="label">
                    Upload Logo
                    <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                  </Button>
                  {logoFile ? <Typography variant="body2">{logoFile.name}</Typography> : null}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={0.5}>
                  <Button type="submit" variant="contained" color="primary" disabled={loading}>
                    {loading ? 'Submitting...' : 'Create Customer'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateCustomers;
